import { ContentColumn, LearnerActivityBase } from '../../../components/shared';
import strings from '../../../../../strings/strings.json';
import { BodyLarge, Headline3 } from '../../../../../components/Elements';
import { APIError, ApiResponse, DOCUMENTS_URL } from '../../../../../lib/_api-helpers';
import { ResourceCardGrid } from '../../../components/ActivityCardGrid';
import MarkAsCompleteCard from '../../../components/MarkAsCompleteCard';
import styled from 'styled-components';
import { sizes } from '../../../../../theme';
import { CourseNavigationActivity } from '../../../components/navigation/CourseNavigationActivity';
import ExpandableContentComponent from 'components/ExpandableContentComponent';
import { BuildMethod } from 'models/Activity';
import AudioPlayer from 'pages/learner/components/AudioPlayer';
import VideoPlayer from 'pages/learner/components/VideoPlayer';
import { LearnerCardBase } from 'pages/learner/sharedStyles/LearnerStyles';
import { LearnerActivity } from 'models/LearnerCourse';
import { useContext } from 'react';
import { LearnerCourseContext } from 'contextProviders/LearnerCourseContext';
import ActivityHeader from '../ActivityHeader';

type LearnerTaskContentProps = {
    task: LearnerActivity;
    toggleTaskCompletion: () => Promise<ApiResponse<boolean>>;
    toggleCompletionErrors: APIError[];
};

export default function LearnerTaskContent({
    task,
    toggleTaskCompletion,
    toggleCompletionErrors,
}: LearnerTaskContentProps): JSX.Element {
    const courseComplete = useContext(LearnerCourseContext).learnerCourse!.completedDate;
    let taskContent: JSX.Element = <></>;

    switch (task.buildMethod) {
        case BuildMethod.TEXT_AND_IMAGE:
            taskContent = (
                <>
                    {task.taskContents.map((x, i) => {
                        if (x.type === 'text') {
                            return (
                                <ContentContainer key={i}>
                                    <Headline3>{strings.taskPage.textContentTitle}</Headline3>
                                    <DescriptionText>{x.text}</DescriptionText>
                                    {x.audio && <AudioPlayer filepath={x.audio} />}
                                </ContentContainer>
                            );
                        } else {
                            //IMAGE
                            return (
                                <ContentContainer key={i}>
                                    <Image src={`${DOCUMENTS_URL}/${x.image}`} alt={x.imageDescription} />
                                    <DescriptionText>
                                        <b>Image description:</b> {x.imageDescription}
                                    </DescriptionText>
                                    {x.audio && <AudioPlayer filepath={x.audio} />}
                                </ContentContainer>
                            );
                        }
                    })}
                </>
            );
            break;
        case BuildMethod.AUDIO:
            taskContent = (
                <>
                    <ContentContainer>
                        <Headline3>{strings.taskPage.audioContentTitle}</Headline3>
                        <AudioPlayer filepath={task.audioDocument!.s3Filepath} />
                    </ContentContainer>
                    {task.content && (
                        <Card>
                            <ExpandableContentComponent
                                headline={strings.taskPage.generatedTextContentTitle}
                                closedHeightInPx={150}
                                content={<DescriptionText>{task.content}</DescriptionText>}
                                expandedByDefault
                            />
                        </Card>
                    )}
                </>
            );
            break;
        case BuildMethod.VIDEO:
            taskContent = (
                <>
                    <ContentContainer>
                        <Headline3>{strings.taskPage.videoContentTitle}</Headline3>
                        <VideoPlayer filepath={task.videoDocument!.s3Filepath} />
                    </ContentContainer>
                    {task.content && (
                        <Card>
                            <ExpandableContentComponent
                                headline={strings.taskPage.generatedTextContentTitle}
                                closedHeightInPx={150}
                                content={<DescriptionText>{task.content}</DescriptionText>}
                            />
                        </Card>
                    )}
                </>
            );
            break;
    }

    return (
        <LearnerActivityBase>
            <ContentColumn>
                <ActivityHeader activity={task} />
                {taskContent}
                {task.resources.length > 0 && <ResourceCardGrid resources={task.resources} />}
            </ContentColumn>
            <TaskContentPageFooter>
                {!courseComplete && (
                    <MarkAsCompleteCard
                        completed={task.completed}
                        toggleTaskCompletion={toggleTaskCompletion}
                        toggleCompletionErrors={toggleCompletionErrors}
                    />
                )}
                <CourseNavigationActivity />
            </TaskContentPageFooter>
        </LearnerActivityBase>
    );
}

const Card = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
`;

const DescriptionText = styled(BodyLarge)`
    white-space: pre-wrap;
`;

export const ContentContainer = styled(LearnerCardBase)`
    padding: ${sizes.spacingMd};
    display: flex;
    flex-direction: column;
    gap: ${sizes.spacingMd};
    max-width: 1000px;
`;

export const Image = styled.img`
    object-fit: contain;
    width: 100%;
    height: auto;
`;

const TaskContentPageFooter = styled.div`
    width: 100%;
`;
